export interface ExercisableOptionsDetails {
  optionHolderId: string;
  optionHolderName: string;
  esopPlanName: string;
  numberOfShares: number;
  dateOfExercise?: string;
  exercisePrice: number;
  isCashExercise: boolean;
  conversionRatio: number;
}
export interface ExerciseReq {
  isCashExercise: boolean;
  isFractional: boolean;
  dateOfExercise: string;
  optionsAvailable: number;
  numberOfShares: number;
  note: string;
  raisedBy: RaisedBy;
  optionHolderId: string;
  optionHolderName: string;
  esopPlanName: string;
  planType: string;
  sellingPrice?: number;
  amountReceived?: number;
  isSellToCover: boolean;
  sharesSoldToCover?: number;
  exercisePrice: number;
  conversionRatio: number;
  fmv?: number;
  identifier: string;
  status: string;
  id: string;
  conversionMethodology?: string;
}

export interface Result {
  resultCode: string;
  reason: string;
}

export interface ApproveExerciseReq {
  isCashExercise: boolean;
  dateOfExercise: string;
  numberOfShares: number;
  note?: string | null;
  optionHolderId: string;
  optionHolderName: string;
  esopPlanName: string;
  exercisePrice: number;
  optionState: string;
}

export interface PlanExercise {
  exerciseDate: string;
  cashExercisedOptions: number;
  stockExercisedOptions: number;
  cumulativeExercisedOptions: number;
}

export enum RaisedBy {
  ADMIN = "ADMIN",
  EMPLOYEE = "EMPLOYEE",
  RESIGNATION = "RESIGNATION",
  ONBOARDING = "ONBOARDING",
}

export enum ConversionMethodology {
  ROUND_UP = "ROUND_UP",
  ROUND_DOWN = "ROUND_DOWN",
  ROUND_OFF = "ROUND_OFF",
}

export interface HistoricExerciseValidateRes {
  valid: boolean;
  event: ExerciseValidationField[];
  offers: ExerciseValidationField[][];
}

export interface ExerciseLetterUrl {
  s3Key: string;
  url: string;
}

export interface ExerciseValidationField {
  name: string;
  value: string | number | ExerciseLetterUrl | null;
  valid: boolean;
  error: string | null;
}

export interface HistoricExerciseImportRes {
  transactionId: string;
}

export interface HistoricExerciseImportReq {
  file?: string;
  correctedEvent?: ExerciseValidationField[];
  correctedOffers?: ExerciseValidationField[][];
  confirm?: boolean;
}

export interface UploadExerciseLetter {
  employeeId: string;
  file: string;
  fileType: "pdf" | "docx";
  fileName?: string;
}

export interface UploadExerciseLettersReq {
  transactionId: string;
  letters: UploadExerciseLetter[];
}

export interface UploadExerciseLetterResult {
  employeeId: string;
  hrId: string;
  s3Path: string;
  presignedUrl: string;
}

export interface UploadExerciseLettersRes {
  uploaded: UploadExerciseLetterResult[];
  failed: { employeeId: string; error: string }[];
}

export type BankStatementSystemField = "referenceId" | "amount" | "paymentDate";

export type BankStatementMapperField = {
  type: "direct";
  field: string;
};

export type BankStatementMapperData = Partial<
  Record<BankStatementSystemField, BankStatementMapperField>
>;

export type BankStatementMapping = {
  headerRow: number;
  mapperData: BankStatementMapperData;
};

export type BankStatementMapper = {
  id: string;
  name: string;
  mapperData: BankStatementMapperData;
};

export type BankStatementPayment = {
  id: string;
  employeeName: string;
  hrId?: string;
  requestId?: string;
  referenceId: string;
  amount: number;
  paymentType: string;
};

export type BankStatementPreviewRow = {
  rowId: string;
  sourceRow: number;
  referenceId: string;
  amount: number | null;
  paymentDate?: string;
  status: string;
  reasons: string[];
  candidatePaymentIds: string[];
  claim?: string;
};

export type BankStatementPreview = {
  rows: BankStatementPreviewRow[];
  pendingPayments: BankStatementPayment[];
  unmatchedPendingPayments: BankStatementPayment[];
  summary: {
    eligible: number;
    needsReview: number;
    unmatched: number;
    excludedDebit: number;
  };
};

export type BankStatementApprovalGroup = {
  paymentType: "exercise price" | "tax price";
  paymentIds: string[];
};

export type BankStatementApprovalFailure = BankStatementApprovalGroup & {
  message: string;
};

export type BankStatementApprovalResult = {
  approvedPaymentIds: string[];
  successfulGroups: BankStatementApprovalGroup[];
  failedGroups: BankStatementApprovalFailure[];
};

export type BankStatementSelection = {
  rowId: string;
  claim: string;
  paymentId: string;
  acknowledgeMismatch?: boolean;
};

export type BankStatementInspection = {
  headersInFile: string[];
  headersInSystem: BankStatementSystemField[];
  prefilledMapperData: BankStatementMapperData;
  sampleData: Record<string, unknown>;
  headerRow: number;
};

export type SaveBankStatementMapperRequest = {
  name: string;
  mapperData: BankStatementMapperData;
};

export type PreviewBankStatementRequest = {
  transactionId: string;
  mapping: BankStatementMapping;
  file: File;
};

export type ApproveBankStatementRequest = {
  transactionId: string;
  selections: BankStatementSelection[];
};
